import CheckCircleSharpIcon from '@mui/icons-material/CheckCircleSharp';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ErrorSharpIcon from '@mui/icons-material/ErrorSharp';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps
} from '@mui/material';
import React, { forwardRef, useEffect, useState } from 'react';
import MaskedInput from 'react-text-mask';
import { CEPRegex, cvvRegex, expiredDate, regexBirthDate, regexCardNumber, regexCPF, regexPhone } from './regex';
import { container, helperStyle, inputStyle, labelStyle } from './styles';
import { InputProps } from './types';

interface CustomProps {
  onChange?: (event: { target: { name: string; value: string } }) => void;
  name?: string;
  mask: InputProps['mask'];
  error?: boolean;
}

const TextMaskCustom = forwardRef(function TextMaskCustom({ mask, ...props }: CustomProps, _ref) {
  const selectMask = () => {
    switch (mask) {
      case 'cpf':
        return regexCPF;
      case 'phone':
        return regexPhone;
      case 'zipcode':
        return CEPRegex;
      case 'cardNumber':
        return regexCardNumber;
      case 'expiredDate':
        return expiredDate;
      case 'cvv':
        return cvvRegex;
      case 'birthDate':
        return regexBirthDate;
      default:
        return regexCPF;
    }
  };
  return (
    <MaskedInput
      {...props}
      mask={selectMask()}
      placeholderChar={'\u2000'} // showMask
    />
  );
});

const ErrorIcon: React.FC = () => (
  <>
    <InputAdornment position="end">
      <ErrorSharpIcon color="error" />
    </InputAdornment>
  </>
);
const SuccessIcon: React.FC = () => (
  <>
    <InputAdornment position="end">
      <CheckCircleSharpIcon color="success" />
    </InputAdornment>
  </>
);
const NoIcon: React.FC = () => (
  <>
    <InputAdornment position="end"></InputAdornment>
  </>
);

const CustomizedInputs = forwardRef(function CustomizedInputs(
  { name, label, helperText, isDirty, IconHelper, disabled, appId, revealable, ...props }: InputProps,
  ref
) {
  const inputProps: Partial<OutlinedInputProps> = props;
  const inputElementAppId = appId ? `${appId}-input-element` : undefined;

  if (props.mask) {
    inputProps.inputComponent = TextMaskCustom as any;
    inputProps.inputProps = {
      mask: props.mask,
      error: props.error,
      ...(inputElementAppId ? { 'data-app-id': inputElementAppId } : {})
    };
  } else if (inputElementAppId) {
    inputProps.inputProps = {
      ...inputProps.inputProps,
      'data-app-id': inputElementAppId
    };
  }
  const [IconComponent, setIconComponent] = useState<React.FC>(NoIcon);

  useEffect(() => {
    if (!isDirty) {
      setIconComponent(NoIcon);
    } else if (helperText && props.error) {
      setIconComponent(ErrorIcon);
    } else {
      setIconComponent(SuccessIcon);
    }
  }, [isDirty, props.error, props.value]);

  const [revealed, setRevealed] = useState(false);
  const canReveal = Boolean(revealable) && props.type === 'password';
  const fieldId = appId ?? (name ? `input-${name}` : undefined);
  const endAdornment = canReveal ? (
    <Box sx={{ alignItems: 'center', display: 'flex' }}>
      <InputAdornment position="end">
        <IconButton
          aria-label={revealed ? 'Ocultar senha' : 'Mostrar senha'}
          data-app-id={appId ? `${appId}-toggle-visibility` : undefined}
          edge="end"
          onClick={() => setRevealed((v) => !v)}
          onMouseDown={(e) => e.preventDefault()}
          size="small"
        >
          {revealed ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
        </IconButton>
      </InputAdornment>
      {IconComponent as unknown as React.ReactNode}
    </Box>
  ) : (
    IconComponent
  );

  return (
    <FormControl data-app-id={appId ? `${appId}-control` : undefined} sx={container} variant="standard">
      <InputLabel
        data-app-id={appId ? `${appId}-label` : undefined}
        htmlFor={fieldId}
        shrink
        sx={{ ...labelStyle, whiteSpace: 'break-spaces', position: 'static', transform: 'none' }}
      >
        {label}
        {IconHelper && <IconHelper />}
      </InputLabel>
      <>
        <OutlinedInput
          data-app-id={appId ? `${appId}-input` : undefined}
          disabled={disabled}
          endAdornment={endAdornment}
          fullWidth
          id={fieldId}
          notched={false}
          ref={ref}
          sx={inputStyle}
          {...inputProps}
          {...(canReveal ? { type: revealed ? 'text' : 'password' } : {})}
        />
        {helperText && props.error && (
          <>
            <FormHelperText sx={helperStyle}>{helperText}</FormHelperText>
          </>
        )}
      </>
    </FormControl>
  );
});

export default CustomizedInputs;
